import { SelectionController } from 'handsontable/selection';
import { CellCoords } from 'handsontable/base';
import { debounce } from 'lodash';
import { EditorState, SelectionState } from 'draft-js';

// Utils
import {
    getEndCol,
    getEndRow,
    getStartCol,
    getStartRow,
    getStartSelectionCoords,
} from '../../../bridge/utils/selectionUtils/tableSelectionAccessors';
import {
    getAllCellsBetween,
    getMaxCellSelection,
    getMinCellSelection,
} from '../../../bridge/utils/selectionUtils/tableSelectionGeometryUtils';
import { getCellRangeName } from '../../utils/selectionUtils/tableSelectionExcelNameUtils';
import { isFormula } from '../../../bridge/utils/dataUtils/formula/tableFormulaPredicateUtils';
import insertTextAt from '../../../../../components/editor/customRichUtils/insertTextAt';
import applyInlineStyle from '../../../../../components/editor/customRichUtils/applyInlineStyle';
import removeAllInlineStyle from '../../../../../components/editor/customRichUtils/removeAllInlineStyle';
import { hasCommandModifier, hasShiftKey } from '../../../../../utils/keyboard/keyboardUtility';
import replaceSelectionWithText from '../../../../../components/editor/customRichUtils/replaceSelectionWithText';
import forceFocus from '../../../../../components/editor/customRichUtils/forceFocus';

// Types
import { CellCoordsObj, CellSelection } from '../../../../../../common/table/TableTypes';
import { MilanoteHotPlugin } from '../MilanoteHotPlugin';
import { CellReference, findCellReferenceBeforeSelection, getCellReferences } from './cellReferences';
import { hasSpecialCharBeforeSelection } from './textHelpers';
import { MNHotInstance } from '../../../bridge/tableHotInstanceTypes';

export const DRAFTJS_FORMULA_CELL_REFERENCE_PLUGIN_NAME = 'DraftJSFormulaCellReference';
const FORMULA_REFERENCE_HIGHLIGHTED_CLASS_NAME = 'formula-reference-highlighted';

class DraftJSFormulaCellReferenceHotPlugin extends MilanoteHotPlugin {
    private prevText: string | null = null; // only used to determine if re-highlight of cell references is needed

    private customBordersAdded = false;

    // Tracks if we are in the process of referencing a cell
    // TODO: Simplify -- this only needs to be an EditorState to support appending text
    private referenceCellsEditorState: EditorState | null = null;

    private cellReferenceAtCursor: CellCoordsObj | null = null;

    private justReferencedCell = false;
    private cellReferences: CellReference[] = [];

    /**
     * This key will be used as a prop key when enabling this plugin in the HotTable component
     */
    static get PLUGIN_KEY(): string {
        return DRAFTJS_FORMULA_CELL_REFERENCE_PLUGIN_NAME;
    }

    /**
     * The `enablePlugin` method is triggered on the `beforeInit` hook.
     * It should contain the plugin's initial setup and hook connections.
     * This method is run only if the `isEnabled` method returns `true`.
     */
    enablePlugin(): void {
        this.addHook('beforeOnCellMouseDown', (...args) => this.onBeforeOnCellMouseDown(...args));
        this.addHook('beforeOnCellMouseOver', (...args) => this.onBeforeOnCellMouseOver(...args));
        this.addHook('beforeChange', () => this.onBeforeChange());

        this.addMilanoteHook('onMilanoteCellEditorChange', (...args) =>
            // @ts-ignore - The official typings use TiptapContent now
            this.debouncedOnMilanoteCellEditorChange(...args),
        );

        // The `super` method sets the `this.enabled` property to `true`.
        // It is a necessary step to update the plugin's settings properly.
        super.enablePlugin();
    }

    /******************
     * PLUGIN HOOKS
     ******************/

    onMilanoteCellEditorChange(editorState: EditorState): void {
        if (!this.milanoteHot.getActiveEditor()?.isOpened()) return;

        // If we referenced a cell using this plugin (using referenceCell(), which updates the Milanote Editor's value),
        // `onMilanoteCellEditorChange` will be called and we should ignore it.
        if (this.justReferencedCell) {
            this.justReferencedCell = false;
            return;
        }

        if (!this.milanoteHot.isListening()) return;

        const currentText = editorState.getCurrentContent().getPlainText();
        const selectionStart = editorState.getSelection().getStartOffset();

        // ***********************************************
        // 1. Ignore if input value is not a formula

        if (!isFormula(currentText)) {
            this.unsetCellReferenceMode();
            this.removeCellSelectionReferences();
            return;
        }

        // ***********************************************
        // 2. Re-highlight all cell references in the formula if text has changed

        if (this.prevText !== currentText) {
            this.highlightCellSelectionReferences(editorState);
        }
        this.prevText = currentText;

        // ***********************************************
        // 3. Determine whether or not to enter cell reference mode (where clicking on another cell will reference it)

        const containsSpecialCharBeforeSelection = hasSpecialCharBeforeSelection(currentText, selectionStart);
        const cellReferenceBeforeSelection = findCellReferenceBeforeSelection(
            this.cellReferences,
            currentText,
            selectionStart,
        );

        if (containsSpecialCharBeforeSelection) {
            // Cursor follows an open paren, comma, operator etc -- click should add a new cell reference
            const newEditorState = replaceSelectionWithText('')(editorState);
            this.setCellReferenceMode(newEditorState);
        } else if (cellReferenceBeforeSelection) {
            // Cursor is on an existing cell reference -- click should replace that cell reference
            this.setReplacementCellReferenceMode(selectionStart, cellReferenceBeforeSelection, editorState);
        } else {
            // Cursor is not at a cell reference or special character -- click should not reference a cell
            this.unsetCellReferenceMode();
        }
    }

    debouncedOnMilanoteCellEditorChange = debounce(this.onMilanoteCellEditorChange, 50);

    onBeforeOnCellMouseDown(
        event: MouseEvent,
        coords: CellCoords,
        TD: HTMLTableCellElement,
        controller: SelectionController,
    ): void {
        // @ts-ignore - Handsontable private property
        if (event.isImmediatePropagationEnabled === false) return;

        if (!this.referenceCellsEditorState) return;

        // If in cell referencing mode, when clicking on another cell, we want to prevent the normal behaviour of
        // selecting the clicked cell, and instead reference the cell in the current textarea.

        event.stopImmediatePropagation();
        event.preventDefault();

        // If shift key is pressed, we want to select the cell range from the cell we started referencing from to
        // the cell we clicked on.
        if (hasShiftKey(event) && !!this.cellReferenceAtCursor) {
            const { row: startRow, col: startCol } = this.cellReferenceAtCursor;
            const { row: endRow, col: endCol } = coords.toObject();

            this.referenceCells([startRow, startCol, endRow, endCol]);
            return;
        }

        if (hasCommandModifier(event) && !!this.cellReferenceAtCursor) {
            const editorState = (this.milanoteHot as MNHotInstance<EditorState>).getMNEditor()!.getEditorState();
            const newEditorState = this.insertTextAtEnd(editorState, ',');
            this.setEditorState(newEditorState);
            this.setCellReferenceMode(newEditorState);
        }

        this.cellReferenceAtCursor = coords.toObject();

        const { row, col } = coords.toObject();
        this.referenceCells([row, col, row, col]);
    }

    onBeforeOnCellMouseOver(
        event: MouseEvent,
        coords: CellCoords,
        TD: HTMLTableCellElement,
        controller: SelectionController,
    ): void {
        if (event.buttons !== 1 || !this.cellReferenceAtCursor) return;

        // If in cell referencing mode and currently referencing cells, when hovering over another cell, we want to
        // prevent the normal behaviour of selecting the cell range, and instead reference the cell range in the
        // current textarea.

        event.stopImmediatePropagation();
        event.preventDefault();

        const { row: startRow, col: startCol } = this.cellReferenceAtCursor;
        const { row: endRow, col: endCol } = coords.toObject();
        this.referenceCells([startRow, startCol, endRow, endCol]);
    }

    onBeforeChange(): void {
        this.unsetCellReferenceMode();
        this.removeCellSelectionReferences();
    }

    /******************
     * HELPER FUNCTIONS
     ******************/

    setEditorState(newEditorState: EditorState): void {
        const editor = (this.milanoteHot as MNHotInstance<EditorState>).getMNEditor();

        if (!editor?.hasValidElement()) return;

        editor.setEditorState(newEditorState);
    }

    insertTextAtEnd(editorState: EditorState, text: string): EditorState {
        const focusKey = editorState.getSelection().getFocusKey();
        const position = editorState.getSelection().getStartOffset();

        const positionSelection = SelectionState.createEmpty(focusKey).merge({
            anchorOffset: position,
            focusOffset: position,
            hasFocus: true,
        });

        return insertTextAt(text)(positionSelection)(editorState);
    }

    /******************
     * ENTERING & EXITING REFERENCE MODE
     ******************/

    setReplacementCellReferenceMode(
        selectionStart: number,
        cellReference: CellReference,
        editorState: EditorState,
    ): void {
        const { cellSelection, cellSelectionName } = cellReference;
        const fromCellCoords = getStartSelectionCoords(cellSelection);

        const focusKey = editorState.getSelection().getFocusKey();
        const positionSelection = SelectionState.createEmpty(focusKey).merge({
            focusOffset: selectionStart,
            anchorOffset: selectionStart - cellSelectionName.length,
            hasFocus: true,
        });
        const newEditorState = replaceSelectionWithText('')(forceFocus(editorState, positionSelection));

        this.setCellReferenceMode(newEditorState, fromCellCoords);
    }

    setCellReferenceMode(editorState: EditorState, fromCellCoords: CellCoordsObj | null = null): void {
        this.referenceCellsEditorState = editorState;
        this.cellReferenceAtCursor = fromCellCoords;
    }

    unsetCellReferenceMode(): void {
        this.referenceCellsEditorState = null;
        this.cellReferenceAtCursor = null;
    }

    /******************
     * ACTUALLY REFERENCING CELLS
     ******************/

    referenceCells(cellSelection: CellSelection): void {
        if (!this.referenceCellsEditorState) return;

        if (
            getStartRow(cellSelection) < 0 ||
            getStartCol(cellSelection) < 0 ||
            getEndRow(cellSelection) < 0 ||
            getEndCol(cellSelection) < 0
        )
            return;

        // Update editor value to include the referenced cell range
        const cellSelectionName = getCellRangeName(cellSelection);
        const newEditorState = this.insertTextAtEnd(this.referenceCellsEditorState, cellSelectionName);

        // Highlight referenced cells
        this.highlightCellSelectionReferences(newEditorState);

        this.justReferencedCell = true;
    }

    /******************
     * CLEARING REFERENCES
     ******************/

    clearReferencedCellHighlights(): void {
        this.cellReferences.forEach(({ cellSelection }) => {
            getAllCellsBetween([cellSelection]).forEach(({ row, col }) => {
                const cellElement = this.milanoteHot.getCell(row, col);
                if (!cellElement) return;

                cellElement.classList.remove(FORMULA_REFERENCE_HIGHLIGHTED_CLASS_NAME);
            });
        });
    }

    removeCellSelectionReferences(): void {
        this.prevText = null;
        this.clearReferencedCellHighlights();
        this.cellReferences = [];

        if (this.customBordersAdded) {
            this.milanoteHot.updateSettings({ customBorders: [] });
            this.customBordersAdded = false;
        }
    }

    /******************
     * ADDING HIGHLIGHTS
     ******************/

    addCellHighlightBorders(references: CellReference[]): void {
        this.customBordersAdded = true;

        this.milanoteHot.updateSettings({
            customBorders: references.map(({ cellSelection, borderColorHex }) => {
                return {
                    range: {
                        from: getMinCellSelection(cellSelection),
                        to: getMaxCellSelection(cellSelection),
                    },
                    left: { width: 2, color: `#${borderColorHex}` },
                    right: { width: 2, color: `#${borderColorHex}` },
                    top: { width: 2, color: `#${borderColorHex}` },
                    bottom: { width: 2, color: `#${borderColorHex}` },
                };
            }),
        });
    }

    addCellHighlightBackgrounds(references: CellReference[]): void {
        references.forEach(({ cellSelection, backgroundColor }) => {
            const cellHighlightColor = backgroundColor;

            const referencedCellCoords = getAllCellsBetween([cellSelection]);
            referencedCellCoords.forEach(({ row, col }) => {
                const cellElement = this.milanoteHot.getCell(row, col);
                if (!cellElement) return;

                cellElement.classList.add(FORMULA_REFERENCE_HIGHLIGHTED_CLASS_NAME);
                cellElement.style.setProperty('--ws-table-cell-reference-overlay-color', cellHighlightColor);
            });
        });
    }

    addCellHighlightTextColors(references: CellReference[], editorState: EditorState): void {
        let lastSearchIndex = 0;
        let newEditorState = removeAllInlineStyle(editorState);

        const editorText = editorState.getCurrentContent().getPlainText();
        references.forEach(({ cellSelectionName, borderColorHex }) => {
            const position = editorText.indexOf(cellSelectionName, lastSearchIndex);
            lastSearchIndex = position + cellSelectionName.length;

            const selection = SelectionState.createEmpty(editorState.getSelection().getFocusKey()).merge({
                anchorOffset: position,
                focusOffset: position + cellSelectionName.length,
            });

            newEditorState = applyInlineStyle(newEditorState, selection, `TEXT_HEX-${borderColorHex}`);
        });

        // Set the undo/redo stack to what it was before, or else each operation to the editorState will be added to the undo stack
        newEditorState = EditorState.set(newEditorState, { undoStack: editorState.getUndoStack() });
        newEditorState = EditorState.set(newEditorState, { redoStack: editorState.getRedoStack() });

        // Apply the editor state changes to the editor
        this.setEditorState(newEditorState);
    }

    /**
     * With the given cellReferences,
     *   1. Add borders and background color to the cells being referenced
     *   2. Inside the editor, apply text color to the cell references
     */
    highlightCellSelectionReferences(editorState: EditorState): void {
        this.clearReferencedCellHighlights();

        const currentText = editorState.getCurrentContent().getPlainText();
        const references = getCellReferences(currentText);

        this.cellReferences = references;

        if (references.length === 0) return;

        this.addCellHighlightBorders(references);
        this.addCellHighlightBackgrounds(references);

        this.addCellHighlightTextColors(references, editorState);
    }
}

export default DraftJSFormulaCellReferenceHotPlugin;
